import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
} from "../checkouts/checkout-tree";
import { ConjunctionMode } from "../segments/segment-types";

// ─────────────────────────────────────────────────────────────────────────────
//   Enums
// ─────────────────────────────────────────────────────────────────────────────

export enum DiscountRuleConditionType {
  CART_TOTAL = "CART_TOTAL",
  CUSTOMER_ORDER_COUNT = "CUSTOMER_ORDER_COUNT",
  PRODUCTS_IN_CART = "PRODUCTS_IN_CART",
  PRODUCT_COLLECTIONS = "PRODUCT_COLLECTIONS",
}

export enum DiscountRuleNumberOperator {
  LESS_THAN = "LESS_THAN",
  LESS_THAN_OR_EQUAL = "LESS_THAN_OR_EQUAL",
  EQUAL = "EQUAL",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_OR_EQUAL = "GREATER_THAN_OR_EQUAL",
}

export const discountRuleNumberOperatorToSymbol: Record<
  DiscountRuleNumberOperator,
  string
> = {
  [DiscountRuleNumberOperator.LESS_THAN]: "<",
  [DiscountRuleNumberOperator.LESS_THAN_OR_EQUAL]: "<=",
  [DiscountRuleNumberOperator.EQUAL]: "=",
  [DiscountRuleNumberOperator.GREATER_THAN]: ">",
  [DiscountRuleNumberOperator.GREATER_THAN_OR_EQUAL]: ">=",
};

export const discountRuleNumberOperatorToFriendlyString: Record<
  DiscountRuleNumberOperator,
  string
> = {
  [DiscountRuleNumberOperator.LESS_THAN]: "less than",
  [DiscountRuleNumberOperator.LESS_THAN_OR_EQUAL]: "less than or equal to",
  [DiscountRuleNumberOperator.EQUAL]: "equal to",
  [DiscountRuleNumberOperator.GREATER_THAN]: "greater than",
  [DiscountRuleNumberOperator.GREATER_THAN_OR_EQUAL]:
    "greater than or equal to",
};

export enum DiscountRuleValueType {
  PERCENTAGE = "PERCENTAGE",
  FIXED_AMOUNT = "FIXED_AMOUNT",
}

export enum DiscountRuleType {
  SHIPPING = "SHIPPING",
}

export enum DeliveryOptionTargetType {
  ALL = "ALL",
  SPECIFIC = "SPECIFIC",
}

export enum DeliveryOptionFilterField {
  TITLE = "TITLE",
  SERVICE_CODE = "SERVICE_CODE",
  DESCRIPTION = "DESCRIPTION",
}

export enum DeliveryOptionFilterOperator {
  IS = "IS",
  IS_NOT = "IS_NOT",
  CONTAINS = "CONTAINS",
}

export const deliveryOptionFilterOperatorToFriendlyString: Record<
  DeliveryOptionFilterOperator,
  string
> = {
  [DeliveryOptionFilterOperator.IS]: "Is",
  [DeliveryOptionFilterOperator.IS_NOT]: "Is not",
  [DeliveryOptionFilterOperator.CONTAINS]: "Contains",
};

export const deliveryOptionFilterFieldToFriendlyString: Record<
  DeliveryOptionFilterField,
  string
> = {
  [DeliveryOptionFilterField.TITLE]: "Option title",
  [DeliveryOptionFilterField.SERVICE_CODE]: "Option service code",
  [DeliveryOptionFilterField.DESCRIPTION]: "Option description",
};

export function conditionTypeToFriendlyString(
  conditionType: DiscountRuleConditionType,
): string {
  switch (conditionType) {
    case DiscountRuleConditionType.CART_TOTAL:
      return "Cart total";
    case DiscountRuleConditionType.CUSTOMER_ORDER_COUNT:
      return "Customer order count";
    case DiscountRuleConditionType.PRODUCTS_IN_CART:
      return "Products in cart";
    case DiscountRuleConditionType.PRODUCT_COLLECTIONS:
      return "Product collections";
    default:
      assertNever(conditionType);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//   Condition schemas (discriminated union on conditionType)
// ─────────────────────────────────────────────────────────────────────────────

export const cartTotalConditionSchema = z.object({
  conditionType: z.literal(DiscountRuleConditionType.CART_TOTAL),
  amount: z.number().positive(),
  currency: createEnum(Currency, { enumName: "Currency" }),
  operator: createEnum(DiscountRuleNumberOperator, {
    enumName: "DiscountRuleNumberOperator",
  }),
});
export type CartTotalCondition = z.infer<typeof cartTotalConditionSchema>;

export const customerOrderCountConditionSchema = z.object({
  conditionType: z.literal(DiscountRuleConditionType.CUSTOMER_ORDER_COUNT),
  count: z.number().int().min(0),
  operator: createEnum(DiscountRuleNumberOperator, {
    enumName: "DiscountRuleNumberOperator",
  }),
});
export type CustomerOrderCountCondition = z.infer<
  typeof customerOrderCountConditionSchema
>;

export const productsInCartConditionSchema = z.object({
  conditionType: z.literal(DiscountRuleConditionType.PRODUCTS_IN_CART),
  productIds: z.array(z.string()).min(1),
  productListOperator: createEnum(SegmentListSingleItemMembershipOperator, {
    enumName: "SegmentListSingleItemMembershipOperator",
  }),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
});
export type ProductsInCartCondition = z.infer<
  typeof productsInCartConditionSchema
>;

export const productCollectionsConditionSchema = z.object({
  conditionType: z.literal(DiscountRuleConditionType.PRODUCT_COLLECTIONS),
  collectionIds: z.array(z.string()).min(1),
  collectionListOperator: createEnum(
    SegmentListMultipleItemMembershipOperator,
    { enumName: "SegmentListMultipleItemMembershipOperator" },
  ),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
});
export type ProductCollectionsCondition = z.infer<
  typeof productCollectionsConditionSchema
>;

// ─────────────────────────────────────────────────────────────────────────────
//   Condition union
// ─────────────────────────────────────────────────────────────────────────────

const discountRuleConditionSchema_ = z.union([
  cartTotalConditionSchema,
  customerOrderCountConditionSchema,
  productsInCartConditionSchema,
  productCollectionsConditionSchema,
]);
export const discountRuleConditionSchema: z.ZodType<DiscountRuleCondition> =
  discountRuleConditionSchema_;
export type DiscountRuleCondition = z.infer<
  typeof discountRuleConditionSchema_
>;

// ─────────────────────────────────────────────────────────────────────────────
//   Two-level AND/OR condition grouping
// ─────────────────────────────────────────────────────────────────────────────

export const discountRuleConditionBlockSchema = z.object({
  conjunction: createEnum(ConjunctionMode, { enumName: "ConjunctionMode" }),
  conditions: z.array(discountRuleConditionSchema),
});
export type DiscountRuleConditionBlock = z.infer<
  typeof discountRuleConditionBlockSchema
>;

export const discountRuleConditionsSchema = z.object({
  conjunction: createEnum(ConjunctionMode, { enumName: "ConjunctionMode" }),
  conditionBlocks: z.array(discountRuleConditionBlockSchema),
});
export type DiscountRuleConditions = z.infer<
  typeof discountRuleConditionsSchema
>;

// ─────────────────────────────────────────────────────────────────────────────
//   Delivery option filter
// ─────────────────────────────────────────────────────────────────────────────

export const deliveryOptionFilterSchema = z.object({
  field: createEnum(DeliveryOptionFilterField, {
    enumName: "DeliveryOptionFilterField",
  }),
  operator: createEnum(DeliveryOptionFilterOperator, {
    enumName: "DeliveryOptionFilterOperator",
  }),
  value: z.string().min(1),
});
export type DeliveryOptionFilter = z.infer<typeof deliveryOptionFilterSchema>;

// ─────────────────────────────────────────────────────────────────────────────
//   Discount value
// ─────────────────────────────────────────────────────────────────────────────

const percentageDiscountValueSchema_ = z.object({
  type: z.literal(DiscountRuleValueType.PERCENTAGE),
  value: z.number().int().min(1).max(100),
});
export const percentageDiscountValueSchema: z.ZodType<PercentageDiscountValue> =
  percentageDiscountValueSchema_;
export interface PercentageDiscountValue extends z.infer<
  typeof percentageDiscountValueSchema_
> {}

const fixedAmountDiscountValueSchema_ = z.object({
  type: z.literal(DiscountRuleValueType.FIXED_AMOUNT),
  value: z.number().positive(),
  currency: createEnum(Currency, { enumName: "Currency" }),
});
export const fixedAmountDiscountValueSchema: z.ZodType<FixedAmountDiscountValue> =
  fixedAmountDiscountValueSchema_;
export interface FixedAmountDiscountValue extends z.infer<
  typeof fixedAmountDiscountValueSchema_
> {}

const discountValueSchema_ = z.union([
  percentageDiscountValueSchema,
  fixedAmountDiscountValueSchema,
]);
export const discountValueSchema: z.ZodType<DiscountValue> =
  discountValueSchema_;
export type DiscountValue = z.infer<typeof discountValueSchema_>;

// ─────────────────────────────────────────────────────────────────────────────
//   Full discount rule
// ─────────────────────────────────────────────────────────────────────────────

export const MAX_CONDITION_BLOCKS = 6;
export const MAX_DELIVERY_OPTION_FILTERS = 25;
export const MAX_DISCOUNT_MESSAGE_LENGTH = 250;

// ─────────────────────────────────────────────────────────────────────────────
//   Type-specific config (discriminated union on discountType)
// ─────────────────────────────────────────────────────────────────────────────

export const shippingTypeConfigSchema = z.object({
  discountType: z.literal(DiscountRuleType.SHIPPING),
  deliveryOptionTarget: createEnum(DeliveryOptionTargetType, {
    enumName: "DeliveryOptionTargetType",
  }),
  deliveryOptionFilters: z
    .array(deliveryOptionFilterSchema)
    .max(MAX_DELIVERY_OPTION_FILTERS)
    .optional(),
});
export type ShippingTypeConfig = z.infer<typeof shippingTypeConfigSchema>;

export const discountRuleTypeConfigSchema = z.union([shippingTypeConfigSchema]);
export type DiscountRuleTypeConfig = z.infer<
  typeof discountRuleTypeConfigSchema
>;

export const discountRuleSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  active: z.boolean(),
  priority: z.number().int().min(0),
  discountMessage: z.string().min(1).max(MAX_DISCOUNT_MESSAGE_LENGTH),
  discount: discountValueSchema,
  conditions: discountRuleConditionsSchema,
  typeConfig: discountRuleTypeConfigSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type DiscountRule = z.infer<typeof discountRuleSchema>;
